import { AuthenticatedImage } from '@components/AuthenticatedImage/AuthenticatedImage';
import { useMediaThumbnail } from '@lib/image/thumbnail';
import { Skeleton } from '@mui/material';
import classNames from 'classnames';
import { memo, useMemo } from 'react';
import { Grid } from 'react-window';
import css from './LibraryRenameModal.module.scss';

const COLUMNS = 3;
const ITEM_SIZE = 64;

const ThumbnailChoice = memo(
  ({
    mediaId,
    libraryId,
    isSelected,
    onSelect,
    onHoverEnter,
    onHoverLeave,
  }: {
    mediaId: string;
    libraryId: string;
    isSelected: boolean;
    onSelect: () => void;
    onHoverEnter: (mediaId: string) => void;
    onHoverLeave: () => void;
  }) => {
    const thumbnailData = useMediaThumbnail(libraryId, mediaId);

    return (
      <div
        className={classNames(css.thumbnailChoiceButton, isSelected && css.selected)}
        onClick={onSelect}
        onMouseEnter={() => onHoverEnter(mediaId)}
      >
        {thumbnailData?.url ? (
          <AuthenticatedImage
            src={thumbnailData.url}
            alt={mediaId}
            className={css.thumbnailImage}
            token={thumbnailData.token}
          />
        ) : (
          <Skeleton variant="rectangular" width={100} height={56.25} sx={{ borderRadius: '8px' }} />
        )}
      </div>
    );
  },
);

ThumbnailChoice.displayName = 'ThumbnailChoice';

const Cell = memo(
  (props: any) => {
    const {
      columnIndex,
      rowIndex,
      style,
      columns,
      folderFiles,
      libraryId,
      selectedMediaId,
      onSelectMedia,
      onHoverEnter,
      onHoverLeave,
    } = props;
    const index = rowIndex * columns + columnIndex;
    if (index >= folderFiles.length) return null;

    const media = folderFiles[index];
    const isSelected = media._id === selectedMediaId;
    return (
      <div style={style} className={css.gridCellWrapper}>
        <ThumbnailChoice
          mediaId={media._id}
          libraryId={libraryId}
          isSelected={isSelected}
          onSelect={() => onSelectMedia(media._id)}
          onHoverEnter={onHoverEnter}
          onHoverLeave={onHoverLeave}
        />
      </div>
    );
  },
  (prevProps: any, nextProps: any) => {
    return (
      prevProps.columnIndex === nextProps.columnIndex &&
      prevProps.rowIndex === nextProps.rowIndex &&
      prevProps.style === nextProps.style &&
      prevProps.selectedMediaId === nextProps.selectedMediaId &&
      prevProps.onHoverEnter === nextProps.onHoverEnter &&
      prevProps.onHoverLeave === nextProps.onHoverLeave
    );
  },
);

Cell.displayName = 'Cell';

export interface LibraryThumbnailGridProps {
  libraryId: string;
  folderFiles: any[];
  selectedMediaId: string;
  onSelectMedia: (mediaId: string) => void;
  gridRef: React.RefObject<any>;
  onHoverChange?: (hoveredMediaId: string | null) => void;
}

export const LibraryThumbnailGrid = memo(
  ({
    libraryId,
    folderFiles,
    selectedMediaId,
    onSelectMedia,
    gridRef,
    onHoverChange,
  }: LibraryThumbnailGridProps) => {
    const handleHoverEnter = (mediaId: string) => {
      onHoverChange?.(mediaId);
    };

    const handleHoverLeave = () => {
      onHoverChange?.(null);
    };

    const gridDimensions = useMemo(() => {
      const columnCount = COLUMNS;
      const rowCount = Math.ceil(folderFiles.length / columnCount);
      return { columnCount, rowCount };
    }, [folderFiles.length]);

    return (
      <div className={css.thumbnailGridContainer} onMouseLeave={handleHoverLeave}>
        <Grid
          gridRef={gridRef}
          columnCount={gridDimensions.columnCount}
          rowCount={gridDimensions.rowCount}
          columnWidth={(ITEM_SIZE * 16) / 9}
          rowHeight={ITEM_SIZE}
          cellComponent={Cell}
          // Out of flow so the preview card beside it, not the grid's full scroll height,
          // sets the picker's height.
          style={{ position: 'absolute', inset: 0 }}
          cellProps={{
            columns: COLUMNS,
            folderFiles,
            libraryId,
            selectedMediaId,
            onSelectMedia,
            onHoverEnter: handleHoverEnter,
          }}
        />
      </div>
    );
  },
);

LibraryThumbnailGrid.displayName = 'LibraryThumbnailGrid';
